import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from "@material-ui/core";
import { OrganizationBookingFailData } from "../../../models/organizationBulkBookingData";
import { useTranslation } from "react-i18next";

/**
 * @interface Props
 */
interface Props {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  anyBookingsSuccessful: boolean;
  existingBookings: OrganizationBookingFailData[];
  mismatchingBookings: OrganizationBookingFailData[];
  miscErrorBookings: OrganizationBookingFailData[];
}

/**
 * A dialog that informs the user about failed booking attempts
 * because of existing bookings or mismatched target groups.
 */
const OrganizationBookingFailureNotedDialog: React.FC<Props> = ({
  open,
  onClose,
  onConfirm,
  anyBookingsSuccessful,
  existingBookings,
  mismatchingBookings,
  miscErrorBookings,
}) => {
  const { t } = useTranslation(["common", "snackbars"]);

  return (
    <Dialog open={open} onClose={onClose} aria-labelledby="failed-booking-dialog-title">
      <DialogContent>
        <h3 id="failed-booking-dialog-title">
          {anyBookingsSuccessful ? "✅ " : "❌ "}
          {anyBookingsSuccessful
            ? t("backendResponses.success.organizationBulkBookingSuccess", {
                ns: "snackbars",
              })
            : t("backendResponses.error.organizationBulkBookingError", {
                ns: "snackbars",
              })}
        </h3>
        <h4 id="failed-booking-dialog-subtitle">
          {t("bulkBookings.failureNotedDialogue.title", { ns: "organization" })}
        </h4>
        {existingBookings.length > 0 && (
          <Box>
            <h4>
              {t("bulkBookings.failureNotedDialogue.existingBookings", {
                ns: "organization",
              })}
            </h4>
            <ul style={{ paddingLeft: "1.2rem" }}>
              {existingBookings.map(({ user_name, event_name }) => (
                <li key={`pre-exist-${user_name}-${event_name}`}>
                  <Typography variant="body2">{`${user_name}, ${event_name}`}</Typography>
                </li>
              ))}
            </ul>
          </Box>
        )}
        {mismatchingBookings.length > 0 && (
          <Box>
            <h4>
              {t("bulkBookings.failureNotedDialogue.mismatchingBookings", {
                ns: "organization",
              })}
            </h4>
            <ul style={{ paddingLeft: "1.2rem" }}>
              {mismatchingBookings.map(({ user_name, event_name }) => (
                <li key={`mismatch-${user_name}-${event_name}`}>
                  <Typography variant="body2">{`${user_name}, ${event_name}`}</Typography>
                </li>
              ))}
            </ul>
          </Box>
        )}
        {miscErrorBookings.length > 0 && (
          <Box>
            <h4>
              {t("bulkBookings.failureNotedDialogue.miscErrorBookings", {
                ns: "organization",
              })}
            </h4>
            <ul style={{ paddingLeft: "1.2rem" }}>
              {miscErrorBookings.map(({ user_name, event_name }) => (
                <li key={`error-${user_name}-${event_name}`}>
                  <Typography variant="body2">{`${user_name}, ${event_name}`}</Typography>
                </li>
              ))}
            </ul>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onConfirm} color="secondary">
          {t("forms.buttons.close", { ns: "common" })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrganizationBookingFailureNotedDialog;
